import React, { useEffect, useState } from "react";
import { Icon } from "@/components/Icon";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDailyReward } from "@/lib/useDailyReward";
import { dailyRewardLabel, dailyRewardTerms, dailyRewardClaimMessage } from "@/lib/dailyReward";
import { useChrome } from "../ui/MobileShell";

/* MobileRewards (design 6a) — three stacked cards, one action each:
     1. Daily reward — amount, eligibility and cooldown from /wallet.bonus;
        POST /wallet/claim-bonus returns the actual credited amounts.
     2. VIP Club      — tier + progress straight from user.vip (honest: no bar
        when progress is unknown; rakeback "rate TBD" since economics aren't set).
     3. Refer & Earn  — code from GET /referrals/me; "Reward per friend TBD" (the
        program's per-friend payout isn't finalized — honest, matches desktop).
   Pushed screen chrome (back + "Rewards"). */

const GREEN = "#00D632";
const SILVER = "#E4E4E7";

export default function MobileRewards() {
  useChrome({ mode: "back", title: "Rewards" });
  const { user } = useAuth();
  const {
    bonus, loading: bonusLoading, error: bonusError, claiming,
    claim: claimReward, reload: loadBonus, countdown: liveCd, canClaim, onCooldown,
  } = useDailyReward();
  const [ref, setRef] = useState(null);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setRef(data)).catch(() => {});
  }, []);

  const vip = user?.vip || null;
  const streak = Number(vip?.daily_streak) || 0;
  const dayInWeek = streak > 0 ? ((streak - 1) % 7) + 1 : 0;

  const claim = async () => {
    setMsg("");
    try {
      const data = await claimReward();
      if (data) setMsg(dailyRewardClaimMessage(data));
    } catch (e) {
      setMsg(apiError(e?.response?.data?.detail) || "Come back later for your next reward.");
    }
  };

  const copyCode = () => {
    if ((ref?.referral_code || ref?.code)) { try { navigator.clipboard.writeText(ref.referral_code); setMsg("Referral code copied"); } catch { /* ignore */ } }
  };
  const shareCode = () => {
    const url = typeof window !== "undefined" ? `${window.location.origin}/login?ref=${(ref?.referral_code || ref?.code) || ""}` : "";
    const text = `Join me on PRYMEBET — free-to-play sportsbook & casino. Use my code ${(ref?.referral_code || ref?.code) || ""}`;
    if (navigator.share) { navigator.share({ text, url }).catch(() => {}); }
    else { try { navigator.clipboard.writeText(`${text} ${url}`); setMsg("Share text copied"); } catch { /* ignore */ } }
  };

  // 7-day strip: done (< today), today (current), later (future/dashed-lock).
  const days = [1, 2, 3, 4, 5, 6, 7].map((d) => ({
    d, done: streak > 0 && d < dayInWeek, today: streak > 0 && d === dayInWeek,
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, padding: "14px 16px 0" }}>
      {/* 1 · Daily reward */}
      <div style={{ borderRadius: 22, padding: 18, background: "radial-gradient(120% 120% at 100% 0%,rgba(0,214,50,.1) 0%,rgba(0,214,50,0) 55%),linear-gradient(180deg,#1A1A1E 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08),inset 0 1px 0 rgba(255,255,255,.12),0 16px 36px rgba(0,0,0,.45)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 40, height: 40, flex: "none", borderRadius: 12, background: "rgba(0,214,50,.1)", boxShadow: "inset 0 0 0 1px rgba(0,214,50,.25)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="pnrcoin" size={22} style={{ color: GREEN }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "800 16px 'Manrope'", color: "#fff" }}>Daily reward</div>
            <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>{streak > 0 ? `Day ${dayInWeek} of 7` : "Start your streak"}</div>
          </div>
        </div>

        {/* 7-day row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6, marginTop: 14 }}>
          {days.map((x) => (
            <div key={x.d} style={{ height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
              background: x.today ? "rgba(0,214,50,.12)" : "#1C1C21",
              boxShadow: x.today ? "inset 0 0 0 1px rgba(0,214,50,.35)" : x.done ? "inset 0 0 0 1px rgba(255,255,255,.05)" : "inset 0 0 0 1px #26262B",
              border: !x.today && !x.done && !x.today ? undefined : undefined }}>
              {x.done ? <Icon name="check-circle" size={16} style={{ color: GREEN }} />
                : x.today ? <Icon name="pnrcoin" size={16} style={{ color: GREEN }} />
                : <Icon name="lock" size={13} style={{ color: "#52525b" }} />}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 14 }}>
          <div style={{ flex: "1 1 200px", minWidth: 0 }}>
            <div style={{ font: "600 11px 'Outfit'", color: "#71717a" }}>{onCooldown ? "Next reward" : "Today"}</div>
            <div className="m-tnum" style={{ font: "800 18px 'Manrope'", color: "#fff" }}>
              {bonusError ? "Reward unavailable" : bonus ? dailyRewardLabel(bonus) : "Loading reward…"}
            </div>
          </div>
          {!onCooldown ? (
            <button data-testid="mobile-rewards-claim" type="button" onClick={bonusError ? loadBonus : claim} disabled={claiming || bonusLoading || (!canClaim && !bonusError)}
              style={{ height: 46, padding: "0 20px", borderRadius: 14, background: GREEN, color: "#0A0A0A", font: "800 14px 'Manrope'", border: 0, cursor: claiming ? "default" : "pointer", opacity: claiming ? 0.6 : 1, boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)", display: "inline-flex", alignItems: "center", gap: 7 }}>
              <Icon name="pnrcoin" size={15} style={{ color: "#0A0A0A" }} /> {bonusError ? "Retry reward" : bonusLoading ? "Loading…" : claiming ? "Claiming…" : (streak > 0 ? `Claim day ${dayInWeek}` : "Claim")}
            </button>
          ) : (
            /* Claimed → live countdown REPLACES the Claim button. */
            <span aria-label={`Next reward in ${liveCd}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, height: 46, padding: "0 16px", borderRadius: 14, background: "rgba(10,10,10,.55)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.08)" }}>
              <Icon name="clock" size={16} style={{ color: GREEN }} />
              <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
                <span style={{ font: "700 8px 'Outfit'", letterSpacing: ".14em", color: "#71717a", textTransform: "uppercase" }}>Next drop in</span>
                <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: "#fff" }}>{liveCd || "soon"}</span>
              </span>
            </span>
          )}
        </div>
        {bonus && <p style={{ marginTop: 10, font: "500 11px 'Outfit'", color: "#a1a1aa" }}>{dailyRewardTerms(bonus)}</p>}
      </div>

      {/* 2 · VIP Club */}
      {vip && (() => {
        const rawProgress = Number(vip.progress);
        const hasProgress = vip.next_tier && Number.isFinite(rawProgress);
        // vip.progress may be a 0–1 fraction or a 0–100 percent — normalize to percent.
        const pct = hasProgress ? Math.max(0, Math.min(100, rawProgress <= 1 ? rawProgress * 100 : rawProgress)) : 0;
        return (
          <div style={{ borderRadius: 22, padding: 18, background: "linear-gradient(180deg,#1A1A1E 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 14px 32px rgba(0,0,0,.4)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 48, height: 48, flex: "none", borderRadius: 14, background: SILVER, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Icon name="vip-fill" size={24} style={{ color: "#0A0A0A" }} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: "600 11px 'Outfit'", color: "#71717a" }}>VIP Club</div>
                <div style={{ font: "800 22px 'Manrope'", letterSpacing: "-.02em", color: "#fff" }}>{vip.tier}{vip.level != null && String(vip.level) !== String(vip.tier) ? ` · Level ${vip.level}` : ""}</div>
              </div>
            </div>

            {hasProgress ? (
              <>
                <div style={{ height: 8, borderRadius: 4, background: "#26262B", overflow: "hidden", marginTop: 14 }}>
                  <div style={{ height: "100%", width: `${pct}%`, borderRadius: 4, background: SILVER }} />
                </div>
                <div className="m-tnum" style={{ marginTop: 8, font: "600 12px 'Outfit'", color: "#a1a1aa" }}>
                  {vip.progressLabel || `${Number(vip.total_wagered || 0).toLocaleString()} wagered · ${Math.round(pct)}% → ${vip.next_tier}`}
                </div>
              </>
            ) : (
              <div className="m-tnum" style={{ marginTop: 12, font: "600 12px 'Outfit'", color: "#a1a1aa" }}>
                {Number(vip.total_wagered || 0).toLocaleString()} wagered{vip.next_tier ? ` · next tier ${vip.next_tier}` : ""}
              </div>
            )}

            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginTop: 14 }}>
              {[bonus?.amount != null ? `Daily reward · ${Number(bonus.amount).toLocaleString()} PCoins` : "Daily reward", "Rakeback · rate TBD", "Birthday bonus"].map((p) => (
                <span key={p} style={{ height: 30, display: "inline-flex", alignItems: "center", padding: "0 11px", borderRadius: 999, background: "#1C1C21", font: "600 12px 'Outfit'", color: "#d4d4d8" }}>{p}</span>
              ))}
            </div>
          </div>
        );
      })()}

      {/* 3 · Refer & earn */}
      <div style={{ borderRadius: 22, padding: 18, background: "linear-gradient(180deg,#1A1A1E 0%,#111114 100%)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.06),inset 0 1px 0 rgba(255,255,255,.09),0 14px 32px rgba(0,0,0,.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 40, height: 40, flex: "none", borderRadius: 12, background: "rgba(0,214,50,.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="refer" size={20} style={{ color: GREEN }} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ font: "800 16px 'Manrope'", color: "#fff" }}>Refer & earn</div>
            <div style={{ font: "500 12px 'Outfit'", color: "#a1a1aa" }}>
              Reward per friend TBD{ref?.count != null ? ` · ${ref.count} joined` : ""}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          <div style={{ flex: 1, height: 48, display: "flex", alignItems: "center", padding: "0 14px", borderRadius: 14, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #26262B" }}>
            <span style={{ font: "800 18px 'Manrope'", letterSpacing: ".08em", color: "#fff" }}>{(ref?.referral_code || ref?.code) || "—"}</span>
          </div>
          <button type="button" onClick={copyCode} aria-label="Copy referral code"
            style={{ width: 48, height: 48, flex: "none", borderRadius: 14, background: "#1C1C21", border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "inset 0 0 0 1px #2F2F35" }}>
            <Icon name="copy" size={18} className="text-white" />
          </button>
          <button type="button" onClick={shareCode}
            style={{ height: 48, flex: "none", display: "flex", alignItems: "center", gap: 7, padding: "0 16px", borderRadius: 14, background: "#F4F4F5", color: "#0A0A0A", font: "800 14px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,.4)" }}>
            <Icon name="share" size={16} style={{ color: "#0A0A0A" }} /> Share
          </button>
        </div>
      </div>

      {msg && <div role="status" style={{ font: "600 12px 'Outfit'", color: "#a1a1aa", textAlign: "center", padding: "2px 0 2px" }}>{msg}</div>}
    </div>
  );
}

